import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '../../hooks.ts';
import {
  applyLampSimilarityMeasurement,
  createVersionFromMarketMatch,
} from '../../store/reducers/workspace.reducer.ts';
import type { RootState } from '../../store/store.ts';
import type { LampSimilarityDetail, RankedCandidate } from '../../types/project.ts';
import { buildCatalogueUrl } from '../../utils/catalogueUrl.ts';
import { MARKET_MATCH_TO_VERSION_FLAG } from '../../utils/featureFlagKeys';
import {
  type MarketMatchPreviewMode,
  selectWorkspaceMeasurementData,
} from '../../utils/measurementCalculation/measurementSource.ts';
import {
  getTargetsSatisfiedCount,
  getTargetsSatisfiedCountFromAggregateResults,
  measurementDataToAggregateResults,
} from '../../utils/normSatisfactionUtils.ts';

type AppliedSelection = { lampId: number; measurementFileId?: number; previewMode: MarketMatchPreviewMode };

function findAppliedCandidate(state: RootState, selection: AppliedSelection | null): RankedCandidate | undefined {
  if (!selection) return undefined;
  const project = state.workspace.project;
  const version = project?.versions.find((v) => v.id === project.activeVersionId);
  return version?.lampSimilarityResult?.candidates.find(
    (c) => c.lampId === selection.lampId && c.measurementFile?.id === selection.measurementFileId,
  );
}

function findAppliedDetail(state: RootState, selection: AppliedSelection | null): LampSimilarityDetail | undefined {
  if (!selection) return undefined;
  return state.workspace.lampSimilarityDetails.find(
    (d) => d.lampId === selection.lampId && d.measurementFileId === selection.measurementFileId,
  );
}

/** The applied market-match preview and what can be done with it (keep it as a version, or unapply). */
export function useAppliedMarketMatch() {
  const dispatch = useAppDispatch();
  const selection = useAppSelector((s) => s.workspace.appliedLampSimilaritySelection);
  const active = useAppSelector((s) => !!findAppliedCandidate(s, selection) && !!findAppliedDetail(s, selection));
  const projectId = useAppSelector((s) => s.workspace.project?.id);
  const activeVersionId = useAppSelector((s) => s.workspace.project?.activeVersionId);
  const isCreatingVersion = useAppSelector((s) => s.workspace.isCreatingVersion);
  const canCreateVersion = useAppSelector((s) =>
    s.featureFlags.userFeatureFlags.includes(MARKET_MATCH_TO_VERSION_FLAG),
  );

  // Re-dispatching the same selection toggles it off in the reducer.
  const unapply = useCallback(() => {
    if (selection) dispatch(applyLampSimilarityMeasurement(selection));
  }, [dispatch, selection]);

  const createVersion = useCallback(() => {
    if (!selection || projectId == null || activeVersionId == null || isCreatingVersion) return;
    dispatch(
      createVersionFromMarketMatch({
        projectId,
        sourceVersionId: activeVersionId,
        lampId: selection.lampId,
        measurementFileId: selection.measurementFileId,
      }),
    );
  }, [dispatch, selection, projectId, activeVersionId, isCreatingVersion]);

  return useMemo(
    () => ({ selection, active, isCreatingVersion, canCreateVersion, unapply, createVersion }),
    [selection, active, isCreatingVersion, canCreateVersion, unapply, createVersion],
  );
}

/**
 * What the applied match looks like: product, targets met (live results when available), and
 * its total lumen output against the optimal configuration. Null while nothing is applied.
 * `RankedCandidate.lumenOutput` is the configuration-level total for the candidate's placements.
 */
export function useAppliedMatchSummary() {
  const { t } = useTranslation();
  const selection = useAppSelector((s) => s.workspace.appliedLampSimilaritySelection);
  const candidate = useAppSelector((s) => findAppliedCandidate(s, selection));
  const detail = useAppSelector((s) => findAppliedDetail(s, selection));
  const lamps = useAppSelector((s) => s.workspace.lamps);
  const modelLayers = useAppSelector((s) => s.workspace.modelLayers);
  const liveMeasurementData = useAppSelector(selectWorkspaceMeasurementData);

  return useMemo(() => {
    if (!selection || !candidate || !detail) return null;
    const idealTotalLumen = sumLumenOutputs(lamps);
    const appliedTotalLumen = candidate.lumenOutput > 0 ? candidate.lumenOutput : null;
    const aggregateResults = liveMeasurementData?.length
      ? measurementDataToAggregateResults(liveMeasurementData)
      : candidate.aggregateResults;
    const targets = aggregateResults?.length
      ? getTargetsSatisfiedCountFromAggregateResults(aggregateResults)
      : getTargetsSatisfiedCount(candidate);
    return {
      selection,
      candidate,
      detail,
      productName: detail.name ?? t('workspace.lampId', { id: selection.lampId }),
      placementCount: candidate.placements?.length ?? 0,
      catalogueUrl:
        detail.seriesId || detail.manufacturerId
          ? buildCatalogueUrl({
              productId: selection.lampId,
              seriesId: detail.seriesId,
              manufacturerId: detail.manufacturerId,
            })
          : null,
      aggregateResults: aggregateResults ?? [],
      // threeJsName → user-facing originalName for the results modal.
      layerDisplayNames: buildLayerDisplayNames(aggregateResults ?? [], modelLayers),
      targets,
      allTargetsSatisfied: targets.satisfied === targets.total,
      idealTotalLumen,
      appliedTotalLumen,
      lumenDeltaPct: computeDeltaPct(appliedTotalLumen, idealTotalLumen),
    };
  }, [t, selection, candidate, detail, lamps, modelLayers, liveMeasurementData]);
}

function sumLumenOutputs(lamps: { lumenOutput?: number }[]): number | null {
  const outputs = lamps.map((l) => l.lumenOutput).filter((v): v is number => typeof v === 'number');
  return outputs.length > 0 ? outputs.reduce((sum, v) => sum + v, 0) : null;
}

function computeDeltaPct(applied: number | null, ideal: number | null): number | null {
  if (applied == null || ideal == null || ideal <= 0) return null;
  return ((applied - ideal) / ideal) * 100;
}

function buildLayerDisplayNames(
  aggregateResults: { name: string }[],
  modelLayers: { threeJsName: string; originalName?: string }[],
): Record<string, string> {
  const map: Record<string, string> = {};
  aggregateResults.forEach((layer) => {
    const match = modelLayers.find((l) => l.threeJsName === layer.name);
    map[layer.name] = match?.originalName || layer.name;
  });
  return map;
}

export const formatLumen = (value: number) => `${Math.round(value).toLocaleString()} lm`;

/** More lumen than the optimal configuration needs reads as a warning, less as good. */
export function getLumenDeltaColor(delta: number): string {
  if (Math.abs(delta) < 0.5) return 'text-gray-400';
  return delta > 0 ? 'text-amber-400' : 'text-lime-400';
}
